import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { eventShare, openWindow } from '@catalogo/core-share';
import { withDevice } from '@catalogo/core-connect/device';
import compose from 'lodash/fp/compose';
import ShareIcon from '@catalogo/theme-svg/share.svg';
import FacebookIcon from '@catalogo/theme-svg/facebook.svg';
import TwitterIcon from '@catalogo/theme-svg/twitter.svg';
import WhatsappIcon from '@catalogo/theme-svg/whatsapp.svg';
import PinterestIcon from '@catalogo/theme-svg/pinterest.svg';
import { withMetrics } from '@catalogo/core-metrics';

const socialList = [
  {
    position: 'top',
    value: 'facebook',
    Icon: FacebookIcon,
  },
  {
    position: 'left-top',
    value: 'twitter',
    Icon: TwitterIcon,
  },
  {
    position: 'left-bottom',
    value: 'whatsapp',
    Icon: WhatsappIcon,
  },
  {
    position: 'bottom',
    value: 'pinterest',
    Icon: PinterestIcon,
  },
];

const renderDesktop = (
  show,
  setShow,
  sizeButton,
  iconSocialSize,
  iconColor,
  share,
  dispatchMetrics,
  hasBoxShadow,
  bgColor,
  Icon,
  sizeIcon
) => (
  <div>
    <WrapperOpen show={show} onClick={() => setShow(false)} sizeButton={sizeButton}>
      {socialList.map(({ Icon, position, value }) => (
        <IconButton position={position} show={show} value={value} onClick={share} key={value}>
          <Icon width={iconSocialSize} height={iconSocialSize} fill={iconColor} />
        </IconButton>
      ))}
    </WrapperOpen>
    <Button
      onClick={() => {
        setShow(!show);
        dispatchMetrics('event:track', { action: `share-button` });
      }}
      sizeButton={sizeButton}
      hasBoxShadow={hasBoxShadow}
      bgColor={bgColor}
      show={show}
    >
      <Icon fill="#888" width={sizeIcon} height={sizeIcon} />
    </Button>
    <Overlay show={show} onClick={() => setShow(false)} />
  </div>
);

const renderTitle = (label, setShow, show, textDecoration, fontSize, fontColor, marginTop) => (
  <Title
    textDecoration={textDecoration}
    fontSize={fontSize}
    fontColor={fontColor}
    marginTop={marginTop}
    onClick={() => setShow(!show)}
  >
    {label}
  </Title>
);

const Share = props => {
  const {
    config,
    icon,
    sizeIcon,
    iconSocialSize,
    sizeButton,
    hasBoxShadow,
    hasHover,
    bgColor,
    label,
    device,
    iconColor,
    dispatchMetrics,
    pHoverColor,
    textDecorationHover,
    svgHoverColor,
    textDecoration,
    fontSize,
    fontColor,
    marginTop,
  } = props;

  const [show, setShow] = useState(false);
  const Icon = icon;
  const { title, text } = config;

  const share = e => {
    const url = config.url || window.location.href;
    setShow(false);
    openWindow(e.currentTarget.value, url, title);
    dispatchMetrics('event:track', { action: `share-button-${e.currentTarget.value}` });
  };

  return (
    <Wrapper pHoverColor={pHoverColor} textDecorationHover={textDecorationHover} svgHoverColor={svgHoverColor}>
      {device === 'mobile' ? (
        <Button
          onClick={() => {
            const url = config.url || window.location.href;
            eventShare(title, text, url);
            dispatchMetrics('event:track', { action: `share-button` });
          }}
          sizeButton={sizeButton}
          hasBoxShadow={hasBoxShadow}
          hasHover={hasHover}
          bgColor={bgColor}
        >
          <Icon fill="#888" width={sizeIcon} height={sizeIcon} />
        </Button>
      ) : (
        renderDesktop(
          show,
          setShow,
          sizeButton,
          iconSocialSize,
          iconColor,
          share,
          dispatchMetrics,
          hasBoxShadow,
          bgColor,
          Icon,
          sizeIcon
        )
      )}
      {label && renderTitle(label, setShow, show, textDecoration, fontSize, fontColor, marginTop)}
    </Wrapper>
  );
};

Share.defaultProps = {
  config: { url: '/', title: '', text: '' },
  sizeIcon: 20,
  sizeButton: 35,
  iconSocialSize: 24,
  hasBoxShadow: true,
  hasHover: true,
  icon: ShareIcon,
  label: '',
  bgColor: '#fff',
  iconColor: '#666',
};

const Button = styled.button`
  ${({ bgColor, sizeButton, hasBoxShadow, theme, show }) => css`
    border-radius: 50%;
    background-color: ${bgColor};
    border: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    outline: none;
    z-index: 1;
    padding: 0;
    cursor: pointer;
    ${
      show &&
      css`
        background-color: ${theme.white};
      `
    }
      ${
        sizeButton &&
        css`
          width: ${sizeButton}px;
          height: ${sizeButton}px;
          flex: 0 0 ${sizeButton}px;
        `
      }
      ${
        hasBoxShadow &&
        css`
          box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 5px;
        `
      }
      
  `}
`;

const Title = styled.p`
  text-decoration: ${({ textDecoration }) => (textDecoration ? textDecoration : 'underline')};
  outline: none;
  border: none;
  background-color: transparent;
  font-size: ${({ fontSize }) => (fontSize ? fontSize : '14px')};
  line-height: 16px;
  color: #666;
  cursor: pointer;
  white-space: nowrap;
  font-family: inherit;
  letter-spacing: -0.2px;
  color: ${({ fontColor }) => (fontColor ? fontColor : '#888')};
  margin-top: ${({ marginTop }) => (marginTop ? marginTop : '10px')};
`;

const Wrapper = styled.div`
  display: flex;
  position: relative;

  &:hover {
    & > p {
      color: ${({ pHoverColor }) => (pHoverColor ? pHoverColor : '#f80032')};
      text-decoration: ${({ textDecorationHover }) => (textDecorationHover ? textDecorationHover : 'none')};
    }

    & > div > button > svg {
      fill: ${({ svgHoverColor }) => (svgHoverColor ? svgHoverColor : '#f80032')};
    }
  }
`;

const Overlay = styled.div`
  ${({ show }) => css`
    background-color: rgba(0, 0, 0, 0.5);
    position: fixed;
    left: 0;
    top: 0;
    width: 100vw;
    height: 100vh;
    z-index: 9;
    transition: all 0.3s ease 0s;

    ${!show &&
    css`
      opacity: 0;
      pointer-events: none;
    `}
  `}
`;

const WrapperOpen = styled.div`
  ${({ show, sizeButton }) => css`
    position: absolute;
    width: ${sizeButton * 3 + 10}px;
    height: ${sizeButton * 3 + 20}px;
    transform: translate(-50%, -50%);
    left: ${sizeButton / 2}px;
    top: ${sizeButton / 2}px;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
    pointer-events: none;

    ${show &&
    css`
      pointer-events: all;
    `};
  `}
`;

const socialPosition = {
  ['top']: () => css`
    top: 0;
    left: 50%;
    transform: translateX(-50%);
  `,
  ['left-top']: () => css`
    top: 20%;
    left: 0;
  `,
  ['left-bottom']: () => css`
    bottom: 20%;
    left: 0;
  `,
  ['bottom']: () => css`
    bottom: 0;
    left: 50%;
    transform: translate(-50%, 0);
  `,
};

const initialSocialPosition = {
  ['top']: () => css`
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  `,
  ['left-top']: () => css`
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  `,
  ['left-bottom']: () => css`
    bottom: 50%;
    left: 50%;
    transform: translate(-50%, 50%);
  `,
  ['bottom']: () => css`
    bottom: 50%;
    left: 50%;
    transform: translate(-50%, 50%);
  `,
};

const IconButton = styled.button`
  ${({ theme, iconSize, show, position }) => css`
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    background: rgb(255, 255, 255);
    border: 0px;
    padding: 5px;
    text-align: center;
    border-radius: 100%;
    transition: all 0.3s ease 0s;
    outline: none;
    opacity: 1;
    box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 5px;
    position: absolute;

    &:hover {
      background-color: ${theme.primaryColor};
      svg {
        fill: #fff;
      }
    }

    ${!show &&
    css`
      ${position && initialSocialPosition[position](iconSize)}
      opacity: 0;
      pointer-events: none;
    `}

    ${position && show && socialPosition[position]()}
  `}
`;

export default compose(withDevice, withMetrics)(Share);
